import React, { useEffect, useState } from 'react';
import axios from 'axios';

/* istanbul ignore next -- REACT_APP_BACKEND_URL is inlined at build time */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function SharesPanel({ token, showToast, refreshVersion }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const refreshShares = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shares`, { headers });
      setShares(response.data.shares || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Could not load your shares', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshShares();
  }, [token, refreshVersion]);

  const revokeShare = async (share) => {
    if (!window.confirm('Revoke this share? The link will stop working.')) return;

    try {
      await axios.delete(`${API_URL}/api/shares/${share.id}`, { headers });
      showToast('Share revoked', 'success');
      await refreshShares();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Could not revoke this share', 'error');
    }
  };

  const copyShareLink = async (share) => {
    const url = `${window.location.origin}/share/${share.id}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard', 'success');
    } catch {
      showToast(`Share link: ${url}`, 'info');
    }
  };

  const activeCount = shares.filter((share) => !share.revoked).length;

  return (
    <section className="bg-white rounded-xl shadow-lg p-6" aria-labelledby="shares-heading">
      <h2 id="shares-heading" className="text-xl font-semibold text-gray-800">My Shares</h2>
      <p className="text-sm text-gray-600 mt-1">
        Public links you have created from your results. Revoke one to take it down.
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {loading ? 'Loading shares…' : `${activeCount} active · ${shares.length - activeCount} revoked`}
      </p>

      {shares.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100">
          {shares.map((share) => (
            <li key={share.id} className="flex items-center justify-between py-2 gap-3">
              <div className="min-w-0 flex-1">
                {!share.revoked ? (
                  <a
                    href={`/share/${share.id}`}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate block"
                  >
                    {window.location.origin}/share/{share.id}
                  </a>
                ) : (
                  <p className="text-sm font-mono text-gray-400 truncate">{share.id}</p>
                )}
                <p className="text-xs text-gray-500">
                  {new Date(share.created_at).toLocaleString()}
                  {share.revoked ? ' · revoked' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!share.revoked && (
                  <button
                    type="button"
                    onClick={() => copyShareLink(share)}
                    className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
                  >
                    Copy
                  </button>
                )}
                {!share.revoked && (
                  <button
                    type="button"
                    onClick={() => revokeShare(share)}
                    className="px-3 py-1 text-sm bg-red-50 text-red-700 rounded-md hover:bg-red-100"
                  >
                    Revoke
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default SharesPanel;
